import { FC, ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Container from './Container';
import BuildNotes from '../../app/build-notes';

interface LayoutProps {
  children: ReactNode;
}

const Layout: FC<LayoutProps> = ({ children }) => {
  return (
    <div className="font-sans bg-gray-50 text-gray-800">
      <Header />
      <main className="py-10 md:py-16">
        <Container>{children}</Container>
      </main>
      <footer className="py-10 md:py-16 border-t border-gray-200">
        <Container>
          <BuildNotes />
          <Footer />
        </Container>
      </footer>
    </div>
  );
};

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/resume', label: 'Resume' },
    { path: '/writing-sample', label: 'Writing Samples' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm">
      <Container>
        <div className="flex items-center justify-between py-4">
          <Link to="/" className="text-2xl font-bold text-gray-900">
            Spencer Hardesty Lewis
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`text-gray-600 hover:text-gray-900 transition ${isActive(path) ? 'font-semibold border-b-2 border-indigo-600' : ''}`}>
                {label}
              </Link>
            ))}
          </nav>
          <div className="md:hidden">
            <button onClick={toggleMobileMenu} className="text-gray-600 hover:text-gray-900" aria-label="Toggle menu">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="h-6 w-6">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden pb-4">
            <nav className="flex flex-col space-y-2">
              {navLinks.map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 px-4 rounded ${isActive(path) ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                  {label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </Container>
    </header>
  );
}

function Footer() {
  return (
    <div className="text-center text-gray-500 text-sm mt-10">
      <p>&copy; {new Date().getFullYear()} Spencer Hardesty Lewis. All Rights Reserved.</p>
      <div className="flex justify-center space-x-4 mt-2">
        <Link to="/terms-and-conditions" className="hover:text-gray-900">Terms & Conditions</Link>
        <span>|</span>
        <Link to="/privacy-policy" className="hover:text-gray-900">Privacy Policy</Link>
      </div>
    </div>
  );
}

export default Layout;
